import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Button, Card, Col, Dropdown, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axiosInstance from '../services/axiosInterceptor';
import Swal from 'sweetalert2';
import useStudyYearSelector from '../discipline/useStudyYearSelector';
import { HealthFlags, outcomeBadge, stayText, thaiDate, timeOf, todayISO } from './nurseOptions';

const StatTile = ({ label, value, variant = 'primary', icon, hint }) => (
  <Card className="card-one h-100">
    <Card.Body className="p-3">
      <div className="d-flex align-items-center gap-2 mb-1">
        <i className={`${icon} text-${variant} fs-20`}></i>
        <span className="fs-xs text-secondary">{label}</span>
      </div>
      <h3 className="card-value mb-0">{value ?? 0}</h3>
      {hint && <div className="fs-xs text-secondary mt-1">{hint}</div>}
    </Card.Body>
  </Card>
);

export default function NurseDashboard() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();

  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`/api/nurse/dashboard?yearId=${yearId}&date=${date}`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [yearId, date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // สถานะคลังยาแยก endpoint จากภาพรวม — เปลี่ยนวันที่แล้วไม่ต้องยิงซ้ำ
  useEffect(() => {
    axiosInstance.get('/api/nurse/stock-summary')
      .then(res => setStock(res.data))
      .catch(() => setStock(null));
  }, []);

  const checkout = (visit) => {
    axiosInstance.post(`/api/nurse/visits/${visit.nv_id}/checkout`, {})
      .then(() => {
        fetchData();
        Swal.fire({ icon: 'success', title: 'ปิดเวลาออกแล้ว', text: visit.st_name, timer: 1400, showConfirmButton: false });
      })
      .catch(err => Swal.fire({ icon: 'error', title: 'ไม่สามารถปิดเวลาออกได้', text: err?.response?.data?.message || '' }));
  };

  const today = data?.today || {};

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ห้องพยาบาล</li>
              <li className="breadcrumb-item active">ภาพรวม</li>
            </ol>
            <h4 className="main-title mb-0">ภาพรวมห้องพยาบาล</h4>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <input type="date" className="form-control" style={{ maxWidth: 170 }}
              value={date} onChange={e => setDate(e.target.value)} />
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="nurse-dash-year">
                <i className="ri-calendar-line me-1"></i>{yearLabel || 'เลือกปีการศึกษา'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map(year => (
                  <Dropdown.Item key={year.year_id} onClick={() => handleSelectYear(year.year_id)}>
                    {`ปีการศึกษา ${year.year_year}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Link to="/nurse/visit" className="btn btn-success text-nowrap">
              <i className="ri-add-line me-1"></i>บันทึกใหม่
            </Link>
          </div>
        </div>

        <Row className="g-3 mb-3">
          <Col xs={6} lg={3}>
            <StatTile label="อยู่ในห้องพยาบาลตอนนี้" value={data?.open?.length} variant="warning" icon="ri-hotel-bed-line" />
          </Col>
          <Col xs={6} lg={3}>
            <StatTile label="มาห้องพยาบาลวันนี้" value={today.visit_count} variant="primary" icon="ri-first-aid-kit-line"
              hint={`${today.student_count || 0} คน`} />
          </Col>
          <Col xs={6} lg={3}>
            <StatTile label="ส่งโรงพยาบาล" value={today.refer_count || 0} variant="danger" icon="ri-hospital-line" />
          </Col>
          <Col xs={6} lg={3}>
            <StatTile label="ส่งกลับบ้าน" value={today.home_count || 0} variant="info" icon="ri-home-4-line" />
          </Col>
        </Row>

        <Row className="g-3">
          <Col xl={7}>
            <Card className="card-one mb-3">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">
                  ยังอยู่ในห้องพยาบาล
                  <span className="badge bg-warning ms-2">{data?.open?.length || 0}</span>
                </Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                {!data?.open?.length ? (
                  <div className="text-center text-secondary py-4 fs-sm">
                    {loading ? 'กำลังโหลด...' : 'ไม่มีนักเรียนค้างอยู่ในห้องพยาบาล'}
                  </div>
                ) : (
                  <Table hover className="mb-0 fs-sm">
                    <tbody>
                      {data.open.map(v => (
                        <tr key={v.nv_id}>
                          <td>
                            <Link to={`/nurse/students/${v.st_id}`} className="fw-semibold">{v.st_name}</Link>
                            <HealthFlags student={v} />
                            <div className="fs-xs text-secondary">
                              ม.{v.c_id}/{v.r_no} · เข้า {timeOf(v.nv_check_in)} น. ({stayText(v.nv_check_in)})
                            </div>
                            <div className="fs-xs">
                              {v.ns_name || v.nv_symptom_detail || '-'}
                              {v.nv_temp ? ` · ${v.nv_temp}°C` : ''}
                              <span className="ms-1">{outcomeBadge(v.nv_outcome)}</span>
                            </div>
                          </td>
                          <td className="text-end align-middle" style={{ width: 96 }}>
                            <Button size="sm" variant="outline-success" onClick={() => checkout(v)}>
                              <i className="ri-logout-box-r-line me-1"></i>ออก
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            <Card className="card-one">
              <Card.Header className="d-flex align-items-center">
                <Card.Title as="h6" className="mb-0">รายการวันที่ {thaiDate(date)}</Card.Title>
                <Link to="/nurse/visits" className="btn btn-sm btn-outline-primary ms-auto">ดูทั้งหมด</Link>
              </Card.Header>
              <Card.Body className="p-0">
                {!data?.recent?.length ? (
                  <div className="text-center text-secondary py-4 fs-sm">ยังไม่มีบันทึกของวันนี้</div>
                ) : (
                  <Table hover className="mb-0 fs-sm">
                    <thead>
                      <tr>
                        <th>เวลา</th>
                        <th>นักเรียน</th>
                        <th>อาการ</th>
                        <th>ผลลัพธ์</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent.map(v => (
                        <tr key={v.nv_id}>
                          <td className="text-nowrap">
                            {timeOf(v.nv_check_in)}
                            {v.nv_check_out && <div className="fs-xs text-secondary">ออก {timeOf(v.nv_check_out)}</div>}
                          </td>
                          <td>
                            <Link to={`/nurse/students/${v.st_id}`}>{v.st_name}</Link>
                            <div className="fs-xs text-secondary">ม.{v.c_id}/{v.r_no}</div>
                          </td>
                          <td>
                            {v.ns_name || '-'}
                            {v.nv_symptom_detail && <div className="fs-xs text-secondary">{v.nv_symptom_detail}</div>}
                          </td>
                          <td>{outcomeBadge(v.nv_outcome)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={5}>
            <Card className="card-one mb-3">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">อาการที่พบใน 7 วันล่าสุด</Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                {!data?.topSymptoms?.length ? (
                  <div className="text-center text-secondary py-4 fs-sm">ยังไม่มีข้อมูล</div>
                ) : (
                  <Table className="mb-0 fs-sm">
                    <tbody>
                      {data.topSymptoms.map(s => (
                        <tr key={s.ns_name}>
                          <td>{s.ns_name}</td>
                          <td className="text-end fw-semibold" style={{ width: 70 }}>{s.cnt}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            {stock && (stock.low_stock > 0 || stock.expiring_soon > 0 || stock.expired > 0) && (
              <Card className="card-one mb-3">
                <Card.Header className="d-flex align-items-center">
                  <Card.Title as="h6" className="mb-0">คลังยาต้องจัดการ</Card.Title>
                  <Link to="/nurse/medicines" className="btn btn-sm btn-outline-primary ms-auto">เปิดคลังยา</Link>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table className="mb-0 fs-sm">
                    <tbody>
                      {stock.low_stock > 0 && (
                        <tr>
                          <td>ต่ำกว่าจำนวนขั้นต่ำ</td>
                          <td className="text-end fw-semibold text-warning">{stock.low_stock} รายการ</td>
                        </tr>
                      )}
                      {stock.expiring_soon > 0 && (
                        <tr>
                          <td>ใกล้หมดอายุใน 60 วัน</td>
                          <td className="text-end fw-semibold text-warning">{stock.expiring_soon} ล็อต</td>
                        </tr>
                      )}
                      {stock.expired > 0 && (
                        <tr>
                          <td>หมดอายุแล้ว (ยังไม่ได้ทิ้ง)</td>
                          <td className="text-end fw-semibold text-danger">{stock.expired} ล็อต</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">มาบ่อยผิดปกติ (≥ 3 ครั้งใน 7 วัน)</Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                {!data?.frequent?.length ? (
                  <div className="text-center text-secondary py-4 fs-sm">ไม่มีนักเรียนที่เข้าเกณฑ์</div>
                ) : (
                  <Table hover className="mb-0 fs-sm">
                    <tbody>
                      {data.frequent.map(s => (
                        <tr key={s.st_id}>
                          <td>
                            <Link to={`/nurse/students/${s.st_id}`}>{s.st_name}</Link>
                            <div className="fs-xs text-secondary">ม.{s.c_id}/{s.r_no} · {s.st_idnumber}</div>
                          </td>
                          <td className="text-end align-middle" style={{ width: 70 }}>
                            <span className="badge bg-danger">{s.cnt} ครั้ง</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </>
  );
}
